import {
    Component,
    OnInit,
    ViewChild,
    Inject
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, forkJoin } from 'rxjs';
import { ItemTypeEnum } from '../../enums/item-type.enum';

import {
    AttachmentService,
    IFileUploadProgressHash,
    FileUploadProgress
} from '../../services/attachment.service';
import { AlertDialogComponent, AlertDialogModel } from '../alert/alert-dialog.component';

// Define a class of required Id values.
export class FileUploadDialogKeyValues {

    public formInstanceElementId: number;
    public attachmentId: number;
    public attachmentTitle: string;

    public dataCollectionId: number;
    public folderId: number;
    public formInstanceId: number;
    public formFieldId: number;
    public attachmentCount: number;
}

// Define a hash of progress/upload values.
export interface IFileUploadProgressValueHash {
    //[fileName: string]: number;
    [fileName: string]: FileUploadProgress;
}

// Define an internally used class.
class FileUploadRequestCallerData {
    public fileName: string;
    public component: FileUploadDialogComponent;
}

class ProgressFileNameAndObservable {
    public fileName: string;
    public observable: Observable<FileUploadProgress>;
}

// Note:  the code in this dialog component was derived from the following tutorial:
//        https://malcoded.com/posts/angular-file-upload-component-with-express/.

@Component({
    selector: 'app-file-upload-dialog',
    templateUrl: './file-upload-dialog.component.html',
    styleUrls: ['./file-upload-dialog.component.scss'],
    standalone: false
})
export class FileUploadDialogComponent implements OnInit {
    // Instance data.
    @ViewChild('file') file;
    //private files: Set<File> = new Set();

    // State-related instance data.
    // Define two related progress hashes and a related array.
    private hshProgressObservables: IFileUploadProgressHash = null;
    private hshProgressValues: IFileUploadProgressValueHash = null;
    private iNumProgressValuesComplete: number = 0;
    //private allProgressFileNamesAndObservables: ProgressFileNameAndObservable[] = null;
    // Define a button text value.
    private primaryButtonText: string = 'Upload';
    // Define status/state values.
    private canBeClosed: boolean = true;
    private showCancelButton: boolean = true;
    private uploading: boolean = false;
    private uploadComplete: boolean = false;
    private titles: object = {};

    // Constructor.
    constructor(private dialogRef: MatDialogRef<FileUploadDialogComponent>,
        private attachmentService: AttachmentService,
        @Inject(MAT_DIALOG_DATA) public initData: FileUploadDialogKeyValues,
        public dialog: MatDialog)
    {
        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        return;
    }

    public setTitle($event: any, i: number) {
        const value = $event.target.value;
        this.titles[i] = value;
    }

    // Accessor methods (used by my .html file).

    // Show controls if multiple files can be uploaded OR, if not, if no files have been uploaded yet
    public get ShowUploadControls(): boolean {
        return (this.AllowMultipleFileSelections || this.files?.length == 0) && (this.uploading != true && this.uploadComplete != true);
    }
    // Only allow multiple files to be uploaded if we're doing an initial upload
    public get AllowMultipleFileSelections(): boolean {
        return this.initData.attachmentId <= 0;
    }
    // Only show the title input if we're doing an initial upload
    public get TitleIsEditable(): boolean {
        return this.initData.attachmentId <= 0 && this.uploadComplete == false;
    }
    public get CurrentAttachmentTitle(): string {
        return this.initData.attachmentTitle;
    }
    public get CurrentAttachmentId(): number {
        return this.initData.attachmentId;
    }
    public get Titles(): object {
        return this.titles;
    }
    public get CanBeClosed(): boolean {
        return (this.canBeClosed);
    }

    public get Uploading(): boolean {
        return (this.uploading);
    }

    public get DisableUpload(): boolean {
        return this.files?.length == 0 || this.uploading;
    }

    public get UploadComplete(): boolean {
        return (this.uploadComplete);
    }

    public get FileType(): string {
        return ItemTypeEnum.FILE;
    }


    files: Array<File> = [];

    public get Files(): Array<File> {
        return (this.files);
    }

    public get Progress(): IFileUploadProgressHash {
        return (this.hshProgressObservables);
    }

    public GetProgressFor(file: File, index: number): number {
        let progressInfo: FileUploadProgress = this.hshProgressValues[file.name];
        let iProgress = progressInfo.progressValue;

        if (iProgress == 100)
            this.deleteFile(index);

        return (iProgress);
    }

    public get ShowCancelButton(): boolean {
        return (this.showCancelButton);
    }

    public get PrimaryButtonText(): string {
        return (this.primaryButtonText);
    }

    // Handle control eventes.
    public addFiles(): void {
        this.file.nativeElement.click();

        return;
    }

    public static fnProgressSubscribor(progressData: FileUploadProgress): void {
        //console.log("Received progress ...");

        // Call into a private method
        // to perform the actual work.
        let component: FileUploadDialogComponent = progressData.callerData;

        component.handleFileUploadProgressUpdate(progressData);

        return;
    }

    public uploadFilesOrCloseDialog(): void {
        if (this.titleNotSupplied()) {
            alert("Title is required for each uploaded file.");
            return;
        }

        // if everything was uploaded already, close the dialog.
        if (this.uploadComplete) {
            this.dialogRef.close(this.hshProgressValues);

            return;
        }

        // set the component state to "uploading"
        this.uploading = true;

        // start the upload and save the progress map
        this.hshProgressObservables = this.attachmentService.uploadFiles(this.initData.formInstanceElementId, this.initData.attachmentId, new Set(this.files), this);

        // convert the progress map into an array
        let allProgressObservables = [];
        this.hshProgressValues = {};
        this.iNumProgressValuesComplete = 0;

        for (let fileName in this.hshProgressObservables) {
            let progressObservable = this.hshProgressObservables[fileName].progress;
            allProgressObservables.push(progressObservable);

            let progressFileNameAndObservable: ProgressFileNameAndObservable =
            {
                fileName: fileName,
                observable: progressObservable
            };

            let initialProgressInfo: FileUploadProgress = new FileUploadProgress();
            this.hshProgressValues[fileName] = initialProgressInfo;
            progressObservable.subscribe(FileUploadDialogComponent.fnProgressSubscribor);
        }

        // Adjust the state variables

        // The OK-button should have the text "Finish" now
        this.primaryButtonText = 'Finish';

        // The dialog should not be closed while uploading
        this.canBeClosed = false;
        this.dialogRef.disableClose = true;

        // Hide the cancel-button
        this.showCancelButton = false;

        forkJoin(allProgressObservables).subscribe(end => {
            this.uploading = false;
            this.uploadComplete = true;
            // PJH - VNEXT-1630
            const alertRef = this.dialog.open(AlertDialogComponent, {
                disableClose: true,
                maxWidth: "600px",
                data: new AlertDialogModel(
                    "Upload Complete",
                    "Click Close to add the uploaded files to the grid.",
                    null, null, true
                )
            });
            alertRef.afterClosed().subscribe(x => {
                this.dialogRef.close(this.hshProgressValues);
            });
        });

        return;
    }

    currentFileUploaded = '';

    // If we're doing a new upload of files, a title must be given to each one
    private titleNotSupplied() {
        return this.TitleIsEditable &&
            this.files.length > Object.keys(this.titles).length;
    }

    // Implement private helper methods.
    private handleFileUploadProgressUpdate(progressData: FileUploadProgress): void {
        // Update this file's progress value.
        if (this.hshProgressValues[progressData.fileName] != null) {

            this.currentFileUploaded = 'Uploading: ' + progressData.fileName;

            this.hshProgressValues[progressData.fileName] = progressData;

            if (progressData.progressValue == 100) {
                this.iNumProgressValuesComplete++;
            }

            this.iNumProgressValuesComplete++;
            if (this.iNumProgressValuesComplete == Object.keys(this.hshProgressValues).length) {
                // ... the dialog can be closed again...
                this.canBeClosed = true;
                this.dialogRef.disableClose = false;

                // ... the upload was successful...
                console.log('the upload of ' + this.iNumProgressValuesComplete + ' was successful');
            }
        }

        return;
    }

    public onFileDropped($event) {
        if ($event.length > 1 && !this.AllowMultipleFileSelections) {
            alert('Only one file can be dropped when uploading a new file for an existing attachment.');
            return;
        }
        this.prepareFilesList($event);
    }

    fileEntered: boolean = false;

    public onFileEntered(passedVal: boolean) {
        this.fileEntered = passedVal;
    }

    public fileSelectHandler(files) {
        this.prepareFilesList(files);
    }

    /**
     * Delete file from files list
     * @param index (File index)
     */
    public deleteFile(index: number) {
        this.files.splice(index, 1);
    }

    /**
     * Convert Files list to normal array list
     * @param files (Files List)
     */
    public prepareFilesList(files: Array<any>) {
        let i: number = 0;
        for (const item of files) {

            this.titles[i++] = item.name;
            //Check if the files array already contains the file
            if (!this.files.some(file => file.name == item.name))
                this.files.push(item);
        }
    }

    /**
     * format bytes
     * @param bytes (File size in bytes)
     * @param decimals (Decimals point)
     */
    public formatBytes(bytes, decimals = 0.0) {
        if (bytes === 0) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }




}
